import { useState, useEffect, useCallback, useRef } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormLabel,
  IconButton,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
  Search,
  UserPlus,
  Building2,
  User,
  Users,
  X,
  Mail,
  Plus,
  Bot,
  ShieldCheck,
  AlertTriangle,
  Factory
} from "lucide-react"

import { ContactsAPI, type Contact } from "@crm-src/lib/contacts-api"
import { customersApi } from "@crm-src/lib/customers-api"
import { leadsApi } from "@crm-src/lib/leads-api"
import { ContactListsAPI, type ContactList } from "@crm-src/lib/contact-lists-api"
import { memberCount } from "@crm-src/pages/contact-groups/membership"
import { aiProspectRecipientsFromLeads, isDeliverableConfidence } from "@crm-src/lib/ai-prospect-recipients"
import {
  fetchAiAudience,
  importAiContacts,
  aiIndustryBreakdown,
  aiContactsForVertical,
  type AiAudienceSource,
  type AiAudienceContact,
} from "@crm-src/lib/ai-audience-api"
import {
  customerRecipients,
  prospectRecipients,
  contactRecipients,
  isValidRecipientEmail,
  isEmailSelected,
  excludeSelectedByEmail,
} from "./email-target-selector/recipients"
import {
  sumGroupContacts,
  sumIndustryContacts,
  buildRecipientSummary,
} from "./email-target-selector/summary"
import { TYPE_PALETTE } from "./email-target-selector/chips"
import {
  ALL_VERTICAL,
  isAllVerticals,
  isVerticalChecked,
  toggleVertical,
  selectedVerticals,
  selectedContactCount,
} from "./email-target-selector/ai-vertical"
import { RecipientList } from "./email-target-selector/contact-list"
import { UnifiedContactList, IndustrySegmentList, GroupList } from "./email-target-selector/browse-lists"
import type { Customer } from "@crm-src/types/customer"
import type { Lead, EmailConfidence } from "@crm-src/types/lead"
import type { AudienceFilters } from "./audience-builder/facets"

export interface EmailRecipient {
  id: string
  name: string
  email: string
  type: 'customer' | 'prospect' | 'manual' | 'contact' | 'ai_prospect'
  company?: string
  /** Only set on AI-prospect rows — the agent's email confidence, used to flag
   * guessed-vs-verified and to default-select only deliverable addresses. */
  email_confidence?: EmailConfidence | null
}

export interface TargetSelection {
  recipients: EmailRecipient[]
  groups?: number[]  // Array of contact list/group IDs
  // Dynamic "by industry" segment: every directory contact in these industries
  // is resolved at send/recount time (crm-api EmailCampaign#recipients_from_
  // target_selection). Stays current as new contacts arrive — no re-editing.
  // Widened (2026-07) to also carry relationship + email-quality facets — see
  // `audience-builder/facets.ts`. `industries` alone is still a valid legacy
  // payload shape.
  filters?: AudienceFilters
}

interface EmailTargetSelectorProps {
  value: TargetSelection
  onChange: (selection: TargetSelection) => void
  disabled?: boolean
  // Emits the human-readable recipient summary ("0 individuals + 1 industry …")
  // so a parent can render it in a header instead of the body — saving vertical
  // space. Fires whenever the selection changes.
  onSummaryChange?: (summary: string) => void
}


export default function EmailTargetSelector({ value, onChange, disabled = false, onSummaryChange }: EmailTargetSelectorProps) {
  const { toast } = useToast()

  // The recipient box is ONE unified field: the same `search` input both searches
  // contacts AND adds a brand-new email — so there is no separate manual-email
  // state; a typed email is read straight off `search`.

  // Browse state (tabs)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [prospects, setProspects] = useState<Lead[]>([])
  // AI-generated CRM leads (sales-agent prospects). Their buyer emails live on
  // customer_contacts, so we keep the raw leads and flatten to contact rows.
  const [aiLeads, setAiLeads] = useState<Lead[]>([])
  const [contactGroups, setContactGroups] = useState<ContactList[]>([])
  const [browseTotals, setBrowseTotals] = useState({ customers: 0, prospects: 0, contacts: 0, ai_prospects: 0 })
  const [browsePages, setBrowsePages] = useState({ customers: 1, prospects: 1, contacts: 1, ai_prospects: 1 })
  const [browseLoadingMore, setBrowseLoadingMore] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [browseTab, setBrowseTab] = useState<'groups' | 'customers' | 'prospects' | 'contacts' | 'ai_prospects' | 'by_industry'>('groups')

  // Per-industry directory-contact counts, lazily fetched the first time the
  // "By industry" tab is opened (null = not loaded yet).
  const [industryCounts, setIndustryCounts] = useState<Record<string, number> | null>(null)
  const [industryCountsLoading, setIndustryCountsLoading] = useState(false)

  const selectedIndustries = value.filters?.industries ?? []

  // Search state (unified server-side)
  const [search, setSearch] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState<EmailRecipient[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const searchAbortRef = useRef<AbortController | null>(null)

  // Get selected groups from value
  const selectedGroups = new Set(value.groups || [])

  const isSearchMode = searchTerm.trim().length > 0

  // Load initial browse data on mount
  const loadContacts = useCallback(async () => {
    try {
      setIsLoading(true)
      // Sort params, 2026-08-28: every call sends the canonical `sort`/`dir`
      // the API actually reads, on `company` — `name` was never a customers
      // sort key, so the old `sort_by: 'name'` was inert. The LEAD calls used
      // to send no sort at all, because `leads_controller` read only
      // `params[:direction]` and ordered by a `customer_contacts` column under
      // the ?search= path's `SELECT DISTINCT customers.*` — Postgres 42P10, a
      // 500 on this picker's search. That is fixed in crm-api (the `Sortable`
      // concern + `leads_sorting_test.rb`), so prospects are now ordered the
      // same way customers are. Keep the "load more" and search calls below on
      // the SAME sort as this initial load, or page 2 arrives from a different
      // ordering than page 1.
      const [customersResponse, prospectsResponse, aiResponse, contactsResponse, groupsResponse] = await Promise.all([
        customersApi.getAll({ per_page: 50, sort: 'company', dir: 'asc' }),
        // Regular prospects EXCLUDE AI-generated ones so the two tabs don't
        // overlap (AI prospects get their own tab, surfaced via contact emails).
        leadsApi.getAll({ per_page: 50, ai_generated: false, sort: 'company', dir: 'asc' }),
        leadsApi.getAll({ per_page: 50, ai_generated: true, sort: 'company', dir: 'asc' }),
        ContactsAPI.getContacts({ per_page: 50, sort: 'name', direction: 'asc' }),
        ContactListsAPI.getAll({ per_page: 100, active: true, sort: 'name', direction: 'asc' })
      ])

      setCustomers(customersResponse.customers || [])
      setProspects(prospectsResponse.leads || [])
      setAiLeads(aiResponse.leads || [])
      setContacts(contactsResponse.data || [])
      setContactGroups(groupsResponse.data || [])
      setBrowseTotals({
        customers: customersResponse.pagination?.total_count ?? (customersResponse.customers || []).length,
        prospects: prospectsResponse.pagination?.total_count ?? (prospectsResponse.leads || []).length,
        ai_prospects: aiResponse.pagination?.total_count ?? (aiResponse.leads || []).length,
        contacts: contactsResponse.meta?.pagination?.total_count ?? contactsResponse.total_count ?? (contactsResponse.data || []).length,
      })
    } catch (error) {
      console.error('Failed to load contacts:', error)
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  // Load data when component mounts
  useEffect(() => {
    loadContacts()
  }, [loadContacts])

  // Load more for browse tabs (pagination)
  const loadMoreBrowse = async (type: 'customers' | 'prospects' | 'contacts' | 'ai_prospects') => {
    const nextPage = browsePages[type] + 1
    setBrowseLoadingMore(type)
    try {
      if (type === 'customers') {
        const res = await customersApi.getAll({ per_page: 50, page: nextPage, sort: 'company', dir: 'asc' })
        setCustomers(prev => [...prev, ...(res.customers || [])])
      } else if (type === 'prospects') {
        const res = await leadsApi.getAll({ per_page: 50, page: nextPage, ai_generated: false, sort: 'company', dir: 'asc' })
        setProspects(prev => [...prev, ...(res.leads || [])])
      } else if (type === 'ai_prospects') {
        const res = await leadsApi.getAll({ per_page: 50, page: nextPage, ai_generated: true, sort: 'company', dir: 'asc' })
        setAiLeads(prev => [...prev, ...(res.leads || [])])
      } else {
        const res = await ContactsAPI.getContacts({ per_page: 50, page: nextPage, sort: 'name', direction: 'asc' })
        setContacts(prev => [...prev, ...(res.data || [])])
      }
      setBrowsePages(prev => ({ ...prev, [type]: nextPage }))
    } catch (error) {
      console.error(`Failed to load more ${type}:`, error)
    } finally {
      setBrowseLoadingMore(null)
    }
  }

  // Server-side search across all entity types
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([])
      setHasSearched(false)
      return
    }

    if (searchAbortRef.current) searchAbortRef.current.abort()
    searchAbortRef.current = new AbortController()

    setSearchLoading(true)
    setHasSearched(true)
    try {
      const [customersResponse, prospectsResponse, aiResponse, contactsResponse] = await Promise.all([
        customersApi.getAll({ search: term, per_page: 50, sort: 'company', dir: 'asc' }),
        leadsApi.getAll({ search: term, per_page: 50, ai_generated: false, sort: 'company', dir: 'asc' }),
        leadsApi.getAll({ search: term, per_page: 50, ai_generated: true, sort: 'company', dir: 'asc' }),
        ContactsAPI.getContacts({ search: term, per_page: 50, sort: 'name', direction: 'asc' })
      ])

      const results: EmailRecipient[] = []
      const seenEmails = new Set<string>()

      for (const c of (customersResponse.customers || [])) {
        if (!c.email || seenEmails.has(c.email.toLowerCase())) continue
        seenEmails.add(c.email.toLowerCase())
        results.push({ id: String(c.id), name: c.full_name || c.email, email: c.email, type: 'customer', company: c.company_name })
      }
      for (const p of (prospectsResponse.leads || [])) {
        if (!p.email || seenEmails.has(p.email.toLowerCase())) continue
        seenEmails.add(p.email.toLowerCase())
        results.push({ id: String(p.id), name: p.full_name || p.email, email: p.email, type: 'prospect', company: p.company_name })
      }
      // AI prospects: flatten matching AI leads to their contact emails so a
      // search surfaces the same rows the AI Prospects tab would.
      for (const r of aiProspectRecipientsFromLeads(aiResponse.leads || [])) {
        if (seenEmails.has(r.email.toLowerCase())) continue
        seenEmails.add(r.email.toLowerCase())
        results.push({ ...r, type: 'ai_prospect' })
      }
      for (const c of (contactsResponse.data || [])) {
        if (!c.email || seenEmails.has(c.email.toLowerCase())) continue
        seenEmails.add(c.email.toLowerCase())
        results.push({ id: `contact_${c.id}`, name: c.full_name || c.email, email: c.email, type: 'contact', company: c.company || undefined })
      }

      setSearchResults(results)
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return
      console.error('Failed to search contacts:', error)
    } finally {
      setSearchLoading(false)
    }
  }, [])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(search), 400)
    return () => clearTimeout(timer)
  }, [search])

  // Trigger server-side search
  useEffect(() => {
    if (searchTerm.trim()) {
      performSearch(searchTerm)
    } else {
      setSearchResults([])
      setHasSearched(false)
    }
  }, [searchTerm, performSearch])

  // Build browse lists from loaded data (for tabs, used only when not searching)
  // NOTE: always fall back name → email. A recipient with a blank name is
  // valid, but a *nil* name once made the crm-api save 422 and the dashboard
  // auto-save SILENTLY drop the whole selection (recipients showed in the
  // editor but never persisted). Keeping a non-empty name here is belt-and-
  // suspenders alongside the backend now treating name as optional.
  const browseCustomerRecipients = customerRecipients(customers)
  const browseProspectRecipients = prospectRecipients(prospects)
  const browseContactRecipients = contactRecipients(contacts)

  // AI prospects are the AI leads' contact emails (the lead's own email is
  // blank), each carrying its confidence so the picker can flag guessed ones.
  const browseAiProspectRecipients: EmailRecipient[] = aiProspectRecipientsFromLeads(aiLeads)
    .map(r => ({ ...r, type: 'ai_prospect' as const }))

  // Filtered search results (exclude already-selected)
  const filteredSearchResults = excludeSelectedByEmail(searchResults, value.recipients)


  // Add recipient - ensures email uniqueness across all types (case-insensitive)
  const addRecipient = (recipient: EmailRecipient) => {
    if (!isEmailSelected(value.recipients, recipient.email)) {
      onChange({
        ...value,
        recipients: [...value.recipients, recipient]
      })
    }
  }


  // Remove recipient
  const removeRecipient = (recipientId: string) => {
    onChange({
      ...value,
      recipients: value.recipients.filter(r => r.id !== recipientId)
    })
  }

  // Add the email typed into the unified search box as a one-off `manual`
  // recipient (crm-api sends recipients[].email for every type — no backend
  // change). The "Send to <email>" action only appears for a complete, not-yet-
  // added address, so this just guards, adds, and clears the box.
  const addTypedEmail = () => {
    const email = search.trim()
    if (!isValidRecipientEmail(email)) return
    if (isEmailSelected(value.recipients, email)) return
    addRecipient({ id: `manual-${email.toLowerCase()}`, name: email, email, type: 'manual' })
    setSearch("")
  }



  // Handle individual contact selection - directly add/remove from recipients
  const handleContactToggle = (contact: EmailRecipient, checked: boolean) => {
    if (checked) {
      addRecipient(contact)
    } else {
      removeRecipient(contact.id)
    }
  }


  // Filter browse contacts by type (exclude already-selected)
  const getBrowseByType = (type: 'customer' | 'prospect' | 'contact' | 'ai_prospect') => {
    const list = type === 'customer' ? browseCustomerRecipients
      : type === 'prospect' ? browseProspectRecipients
      : type === 'ai_prospect' ? browseAiProspectRecipients
      : browseContactRecipients
    return list.filter(c => !value.recipients.some(r => r.id === c.id))
  }

  // Add a set of recipients, skipping any whose email is already selected.
  const addRecipients = (toAddRaw: EmailRecipient[]) => {
    const toAdd = excludeSelectedByEmail(toAddRaw, value.recipients)
    if (toAdd.length > 0) {
      onChange({ ...value, recipients: [...value.recipients, ...toAdd] })
    }
  }

  // Select all visible contacts of a type
  const selectAllOfType = (type: 'customer' | 'prospect' | 'contact' | 'ai_prospect') => {
    addRecipients(getBrowseByType(type))
  }

  // AI prospects only: add only the deliverable (verified/likely) addresses —
  // the safe default so a campaign never blasts guessed emails by accident.
  const selectVerifiedAiProspects = () => {
    addRecipients(getBrowseByType('ai_prospect').filter(r => isDeliverableConfidence(r.email_confidence)))
  }

  const aiVerifiedAvailable = getBrowseByType('ai_prospect')
    .filter(r => isDeliverableConfidence(r.email_confidence)).length

  // AI Prospects tab has THREE recipient pools. "crm" = the qualified AI
  // prospects already in the CRM (browsed above). The other two ("harvester" /
  // "sales_leads") live only in the sales agent: we fetch the pool ONCE (cache),
  // break it down BY INDUSTRY (the agent's verticals), and let the operator add
  // one or more industries — each lands in its own per-industry CRM group
  // (expanded + deduped at send) so a campaign can target just that industry.
  // "All industries" is the friendly default (the whole pool). It's an EXPLICIT
  // sentinel in the set — never inferred from an empty set — so it can actually
  // be toggled off (an empty set means "nothing picked", not "all"). See
  // ./email-target-selector/ai-vertical.ts.
  const [aiSource, setAiSource] = useState<'crm' | AiAudienceSource>('crm')
  const [aiCache, setAiCache] = useState<Partial<Record<AiAudienceSource, AiAudienceContact[]>>>({})
  const [aiLoading, setAiLoading] = useState<AiAudienceSource | null>(null)
  const [aiError, setAiError] = useState<Partial<Record<AiAudienceSource, string>>>({})
  const [aiSelected, setAiSelected] = useState<Record<AiAudienceSource, Set<string>>>({
    harvester: new Set([ALL_VERTICAL]),
    sales_leads: new Set([ALL_VERTICAL]),
  })
  const [aiBusy, setAiBusy] = useState<AiAudienceSource | null>(null)
  const [aiResult, setAiResult] = useState<Partial<Record<AiAudienceSource, string>>>({})

  const aiErrorMessage = (err: unknown): string => {
    const code = err instanceof Error ? err.message : 'AI_AUDIENCE_FETCH_FAILED'
    return code === 'SALES_AGENT_NOT_CONFIGURED' ? 'The sales agent is not configured on this server.'
      : code === 'SALES_AGENT_UNREACHABLE' ? 'The sales agent is unreachable right now.'
      : code === 'SALES_AGENT_BOOTSTRAPPING' ? 'The sales agent is still starting up — try again shortly.'
      : code === 'SALES_AGENT_NO_DATA_YET' ? 'The sales agent has not surfaced any contacts yet.'
      : 'Could not load these AI contacts.'
  }

  // Lazily fetch the pool the first time a bulk AI source is opened (cached).
  const loadAiSource = useCallback(async (source: AiAudienceSource) => {
    setAiLoading(source)
    setAiError(prev => ({ ...prev, [source]: undefined }))
    try {
      const res = await fetchAiAudience(source)
      setAiCache(prev => ({ ...prev, [source]: res.contacts }))
    } catch (err) {
      setAiError(prev => ({ ...prev, [source]: aiErrorMessage(err) }))
    } finally {
      setAiLoading(null)
    }
  }, [])

  useEffect(() => {
    if (aiSource === 'crm') return
    if (aiCache[aiSource] || aiLoading === aiSource || aiError[aiSource]) return
    loadAiSource(aiSource)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSource])

  const toggleAiVertical = (source: AiAudienceSource, vertical: string, checked: boolean) => {
    setAiSelected(prev => ({ ...prev, [source]: toggleVertical(prev[source], vertical, checked) }))
  }

  const handleImportAiSelected = async (source: AiAudienceSource) => {
    if (aiBusy) return
    const contacts = aiCache[source]
    if (!contacts) return
    const selected = aiSelected[source]
    // Each entry becomes one import → one per-industry group (or the whole pool
    // when "All industries" is picked).
    const jobs: Array<{ vertical?: string; subset: AiAudienceContact[] }> = isAllVerticals(selected)
      ? [{ subset: contacts }]
      : selectedVerticals(selected).map(v => ({ vertical: v, subset: aiContactsForVertical(contacts, v) }))

    setAiBusy(source)
    try {
      const addedGroupIds: number[] = []
      let totalContacts = 0
      let totalNew = 0
      for (const job of jobs) {
        if (job.subset.length === 0) continue
        const outcome = await importAiContacts(source, job.subset, job.vertical)
        if (outcome.group && !addedGroupIds.includes(outcome.group.id)) {
          addedGroupIds.push(outcome.group.id)
          totalContacts += outcome.group.members_count
          totalNew += outcome.created
        }
      }
      if (addedGroupIds.length === 0) {
        const msg = 'No emailable contacts found for this selection.'
        setAiResult(prev => ({ ...prev, [source]: msg }))
        toast({ title: 'Nothing to add', description: msg })
        return
      }
      // Refresh the group list so the new per-industry groups render as chips,
      // then select them all.
      try {
        const groupsResponse = await ContactListsAPI.getAll({ per_page: 200, active: true, sort: 'name', direction: 'asc' })
        setContactGroups(groupsResponse.data || [])
      } catch {
        // Non-fatal: the groups are still selected + will send.
      }
      const current = new Set(value.groups ?? [])
      const merged = [...(value.groups ?? [])]
      for (const id of addedGroupIds) if (!current.has(id)) merged.push(id)
      onChange({ ...value, groups: merged })

      const summary = `Added ${addedGroupIds.length} group${addedGroupIds.length === 1 ? '' : 's'} · ${totalContacts.toLocaleString()} contacts${totalNew > 0 ? ` (${totalNew.toLocaleString()} new)` : ''}`
      setAiResult(prev => ({ ...prev, [source]: summary }))
      toast({ title: 'AI contacts added', description: summary })
    } catch (err) {
      const msg = aiErrorMessage(err)
      setAiResult(prev => ({ ...prev, [source]: msg }))
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setAiBusy(null)
    }
  }

  const clearAllRecipients = () => onChange({ ...value, recipients: [] })
  // Past this many individual recipients we collapse the chip cloud to a summary
  // — a bulk AI-source add can land hundreds of rows, and rendering that many
  // chips is both slow and useless to scan.
  const MANY_RECIPIENTS = 40

  // The Sales AI leads / Harvester sub-panel: load the pool, break it down by
  // the agent's industry verticals, and add the picked industries as groups.
  const renderAiSourcePanel = (source: AiAudienceSource) => {
    const contacts = aiCache[source]
    const loading = aiLoading === source
    const error = aiError[source]
    const busy = aiBusy === source
    const selected = aiSelected[source]
    const label = source === 'harvester' ? 'harvester' : 'sales AI lead'
    const Icon = source === 'harvester' ? Factory : Bot

    if (loading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", fontSize: 13 }}>
            <CircularProgress size={14} color="inherit" /> Loading the {label} pool…
          </Box>
          {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" sx={{ height: 40 }} />)}
        </Box>
      )
    }
    if (error) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 2 }}>
          <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>
          <Box>
            <Button type="button" size="small" variant="outlined" color="inherit" onClick={() => loadAiSource(source)} sx={{ fontSize: 12 }}>
              Try again
            </Button>
          </Box>
        </Box>
      )
    }
    if (!contacts) return null

    const breakdown = aiIndustryBreakdown(contacts)
    const total = contacts.length
    const allChecked = isAllVerticals(selected)
    const selCount = selectedContactCount(selected, breakdown, total)

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Box sx={{ color: "text.secondary", mt: 0.25, display: "flex" }}><Icon size={16} /></Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {source === 'sales_leads'
              ? `All ${total.toLocaleString()} named buyers with an email on the sales agent's AI leads, by industry.`
              : `All ${total.toLocaleString()} harvested contacts with an email — the whole pool, by industry.`}
            {" Every one is synced into the CRM each hour, so all of them can be mailed. Pick one or more industries — each becomes its own group your campaign can target."}
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 220, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Box
            onClick={() => toggleAiVertical(source, ALL_VERTICAL, !allChecked)}
            sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, cursor: "pointer", borderBottom: 1, borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}
          >
            <Checkbox size="small" checked={allChecked} onChange={() => {}} sx={{ p: 0.25, pointerEvents: "none" }} />
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>All industries</Box>
            <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{total.toLocaleString()}</Box>
          </Box>
          {breakdown.map(b => {
            const checked = isVerticalChecked(selected, b.vertical)
            return (
              <Box
                key={b.vertical}
                onClick={() => toggleAiVertical(source, b.vertical, !checked)}
                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
              >
                <Checkbox size="small" checked={checked} onChange={() => {}} sx={{ p: 0.25, pointerEvents: "none" }} />
                <Box component="span" sx={{ fontSize: 13, flex: 1 }}>{b.label}</Box>
                <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{b.count.toLocaleString()}</Box>
              </Box>
            )
          })}
        </Box>

        <Box>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            disabled={disabled || busy || selCount === 0}
            onClick={() => handleImportAiSelected(source)}
            startIcon={busy ? <CircularProgress size={12} color="inherit" /> : <Plus size={12} />}
            sx={{ fontSize: 12 }}
          >
            {busy ? 'Adding…' : `Add ${selCount.toLocaleString()} contact${selCount === 1 ? '' : 's'}`}
          </Button>
        </Box>
        {aiResult[source] && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{aiResult[source]}</Typography>
        )}
      </Box>
    )
  }

  // Handle group selection toggle
  const handleGroupToggle = (groupId: number, checked: boolean) => {
    const currentGroups = value.groups || []
    let newGroups: number[]

    if (checked) {
      newGroups = [...currentGroups, groupId]
    } else {
      newGroups = currentGroups.filter(id => id !== groupId)
    }

    onChange({
      ...value,
      groups: newGroups
    })
  }

  // Get total estimated recipients including groups
  const getEstimatedGroupRecipients = () => sumGroupContacts(value.groups || [], contactGroups)

  // Lazily fetch the per-industry directory-contact counts the first time the
  // "By industry" tab is opened. One light (per_page:1) call per industry — fine
  // for an operator tool, and cached for the session.
  useEffect(() => {
    if (browseTab !== 'by_industry' || industryCounts !== null || industryCountsLoading) return
    let cancelled = false
    setIndustryCountsLoading(true)
    // ONE call: the server returns ONLY the industries that actually have emailable
    // contacts, with real stored labels + counts — so the picker can't offer an
    // empty industry or a label that doesn't match what's stored ("Oil & Gas" vs
    // "Oil And Gas"). Replaces N per-industry probes over a hardcoded list.
    ContactsAPI.getIndustries()
      .then((rows) => {
        if (cancelled) return
        setIndustryCounts(Object.fromEntries(rows.map((r) => [r.industry, r.count])))
      })
      .catch(() => { if (!cancelled) setIndustryCounts({}) })
      .finally(() => { if (!cancelled) setIndustryCountsLoading(false) })
    return () => { cancelled = true }
    // NOTE: do NOT depend on `industryCountsLoading` — the effect sets it true,
    // which would re-run the effect, whose cleanup sets cancelled=true, so the
    // in-flight fetch skips setIndustryCountsLoading(false) and the skeleton spins
    // forever. browseTab + industryCounts are the only real triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseTab, industryCounts])

  // Toggle an industry in the dynamic segment.
  const handleIndustryToggle = (industry: string, checked: boolean) => {
    const next = checked
      ? [...selectedIndustries, industry]
      : selectedIndustries.filter(i => i !== industry)
    const nextSelection: TargetSelection = { ...value }
    if (next.length > 0) {
      nextSelection.filters = { industries: next }
    } else {
      delete nextSelection.filters
    }
    onChange(nextSelection)
  }

  // Estimated recipients reachable via the selected industry segment.
  const getEstimatedIndustryRecipients = () => sumIndustryContacts(selectedIndustries, industryCounts)

  // Human-readable recipient summary — emitted to the parent so it can live in
  // the section HEADER (right side) instead of taking a row in the body.
  const summaryText = buildRecipientSummary({
    recipientCount: value.recipients.length,
    groupCount: value.groups?.length || 0,
    estimatedGroupContacts: getEstimatedGroupRecipients(),
    industryCount: selectedIndustries.length,
    estimatedIndustryContacts: getEstimatedIndustryRecipients(),
  })
  useEffect(() => {
    onSummaryChange?.(summaryText)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryText])

  // The text typed into the unified field, when it's a complete email address.
  // `typedEmail` = addable (valid + not already a recipient) → drives the
  // auto-guided "Send to <email>" action; `typedEmailAlreadyAdded` shows a gentle
  // "already a recipient" note instead.
  const trimmedSearch = search.trim()
  const isTypedEmail = isValidRecipientEmail(trimmedSearch)
  const typedEmailAlreadyAdded = isTypedEmail && isEmailSelected(value.recipients, trimmedSearch)
  const typedEmail = isTypedEmail && !typedEmailAlreadyAdded ? trimmedSearch : null

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Selected Groups */}
      {(value.groups?.length || 0) > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>Selected Groups:</FormLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {value.groups?.map((groupId) => {
              const group = contactGroups.find(g => g.id === groupId)
              if (!group) return null
              return (
                <Box
                  key={groupId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    color: "warning.dark",
                    bgcolor: (t) => alpha(t.palette.warning.main, 0.18),
                  }}
                >
                  <Users size={8} />
                  <Box component="span" sx={{ fontSize: 12 }}>
                    {group.name} ({memberCount(group)})
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleGroupToggle(groupId, false)}
                    disabled={disabled}
                    sx={{ p: 0.25, color: "inherit" }}
                  >
                    <X size={8} />
                  </IconButton>
                </Box>
              )
            })}
          </Box>
        </Box>
      )}

      {/* Selected Industries (dynamic segment) */}
      {selectedIndustries.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>Industry segments (auto-include all matching contacts):</FormLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedIndustries.map((industry) => (
              <Box
                key={industry}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  color: "info.dark",
                  bgcolor: (t) => alpha(t.palette.info.main, 0.18),
                }}
              >
                <Factory size={8} />
                <Box component="span" sx={{ fontSize: 12 }}>
                  {industry}{industryCounts ? ` (${industryCounts[industry] ?? 0})` : ''}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleIndustryToggle(industry, false)}
                  disabled={disabled}
                  sx={{ p: 0.25, color: "inherit" }}
                >
                  <X size={8} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Selected Recipients */}
      {value.recipients.length > 0 && value.recipients.length > MANY_RECIPIENTS && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>Individual Recipients:</FormLabel>
            <Button
              type="button"
              variant="text"
              color="inherit"
              size="small"
              onClick={clearAllRecipients}
              disabled={disabled}
              sx={{ fontSize: 12 }}
            >
              Clear all
            </Button>
          </Box>
          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1,
              bgcolor: "surfaceSoft",
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            {value.recipients.length.toLocaleString()} individual recipients selected.
            Duplicate emails are removed automatically before sending.
          </Box>
        </Box>
      )}
      {value.recipients.length > 0 && value.recipients.length <= MANY_RECIPIENTS && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>Individual Recipients:</FormLabel>
          <Box sx={{ maxHeight: 128, overflow: "auto" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {value.recipients.map((recipient) => {
                const pal = TYPE_PALETTE[recipient.type]
                return (
                  <Box
                    key={recipient.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      color: pal ? `${pal}.dark` : "text.secondary",
                      bgcolor: pal ? (t) => alpha((t.palette as any)[pal].main, 0.16) : "surfaceSoft",
                    }}
                  >
                    <Box component="span" sx={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      {recipient.type === 'manual' ? recipient.email : `${recipient.name || recipient.email || 'Unknown'} (${recipient.email})`}
                      {recipient.type === 'customer' && <Building2 size={8} />}
                      {recipient.type === 'prospect' && <UserPlus size={8} />}
                      {recipient.type === 'ai_prospect' && <Bot size={8} />}
                      {recipient.type === 'contact' && <User size={8} />}
                      {recipient.type === 'manual' && <Mail size={8} />}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeRecipient(recipient.id)}
                      disabled={disabled}
                      sx={{ p: 0.25, color: "inherit" }}
                    >
                      <X size={8} />
                    </IconButton>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Contact selection — ONE unified field: the same box searches your
          contacts AND adds a brand-new email. As you type, contacts match below;
          the instant the text is a complete email address an obvious "Send to
          <email>" action appears, so adding an outside address is dead-simple. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Type a name to search, or a full email to add someone new…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (typedEmail) addTypedEmail()
              }
            }}
            disabled={disabled || isLoading}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ color: typedEmail ? "primary.main" : "text.disabled", display: "flex" }}>
                    {typedEmail ? <Mail size={16} /> : <Search size={16} />}
                  </Box>
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 0.25 }}>
            Search your contacts below — or type a full email address to add someone who isn&apos;t in your contacts.
          </Typography>

          {/* Auto-guided "add this email" — appears the instant the text is a complete email */}
          {typedEmail ? (
            <Box
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => { if (!disabled) addTypedEmail() }}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) { e.preventDefault(); addTypedEmail() } }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.25,
                borderRadius: 1,
                border: 1,
                borderColor: "primary.main",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                cursor: disabled ? "default" : "pointer",
                transition: "background-color 120ms",
                "&:hover": disabled ? {} : { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, bgcolor: "primary.main", color: "primary.contrastText", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={16} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "primary.main", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  Send to “{typedEmail}”
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Not in your contacts — click or press Enter to add
                </Typography>
              </Box>
            </Box>
          ) : typedEmailAlreadyAdded ? (
            <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 0.25 }}>
              “{search.trim()}” is already a recipient.
            </Typography>
          ) : null}
        </Box>

        {/* Search mode: unified server-side results */}
        {isSearchMode ? (
          searchLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" sx={{ height: 48 }} />
              ))}
            </Box>
          ) : filteredSearchResults.length === 0 && hasSearched ? (
            // When the user typed an email, the prominent "Send to <email>" card
            // above IS the call-to-action — don't also show "No contacts found".
            typedEmail ? null : (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Box sx={{ mx: "auto", width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, bgcolor: "surfaceSoft" }}>
                  <Box sx={{ color: "text.disabled", display: "flex" }}><Search size={24} /></Box>
                </Box>
                <Typography sx={{ fontSize: 14 }}>No contacts found</Typography>
                <Typography sx={{ fontSize: 12, mt: 0.5 }}>Try adjusting your search criteria</Typography>
              </Box>
            )
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, borderBottom: 1, borderColor: "divider" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>
                  Search Results ({filteredSearchResults.length})
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    const recipientsToAdd = filteredSearchResults.filter(contact =>
                      !value.recipients.some(r =>
                        r.email && contact.email && r.email.toLowerCase() === contact.email.toLowerCase()
                      )
                    )
                    if (recipientsToAdd.length > 0) {
                      onChange({ ...value, recipients: [...value.recipients, ...recipientsToAdd] })
                    }
                  }}
                  startIcon={<Plus size={12} />}
                  sx={{ fontSize: 12 }}
                >
                  Select All ({filteredSearchResults.length})
                </Button>
              </Box>

              <UnifiedContactList
                contacts={filteredSearchResults}
                onContactToggle={handleContactToggle}
                recipients={value.recipients}
                isLoading={false}
              />
            </Box>
          )
        ) : (
          /* Browse mode: category tabs */
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={browseTab}
              onChange={(_, v) => setBrowseTab(v)}
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  minWidth: 0,
                  px: 1,
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <Tab value="groups" label={`Groups (${contactGroups.length})`} />
              <Tab value="by_industry" label="By Industry" />
              <Tab value="customers" label={`Customers (${browseTotals.customers})`} />
              <Tab value="prospects" label={`Prospects (${browseTotals.prospects})`} />
              <Tab value="ai_prospects" label={`AI Prospects (${browseTotals.ai_prospects})`} />
              <Tab value="contacts" label={`Contacts (${browseTotals.contacts})`} />
            </Tabs>

            {/* Groups List */}
            {browseTab === 'groups' && (
              <Box sx={{ pt: 2 }}>
                <GroupList
                  groups={contactGroups}
                  selectedGroups={selectedGroups}
                  onGroupToggle={handleGroupToggle}
                  isLoading={isLoading}
                />
              </Box>
            )}

            {/* By Industry — dynamic segment */}
            {browseTab === 'by_industry' && (
              <Box sx={{ pt: 2 }}>
                <IndustrySegmentList
                  selected={selectedIndustries}
                  counts={industryCounts}
                  loading={industryCountsLoading}
                  onToggle={handleIndustryToggle}
                />
              </Box>
            )}

            {/* Customers List */}
            {browseTab === 'customers' && (
              <Box sx={{ pt: 2 }}>
                <RecipientList
                  contacts={getBrowseByType('customer')}
                  onContactToggle={handleContactToggle}
                  recipients={value.recipients}
                  isLoading={isLoading}
                  type="customer"
                  onSelectAll={() => selectAllOfType('customer')}
                  hasMore={customers.length < browseTotals.customers}
                  loadedCount={customers.length}
                  totalCount={browseTotals.customers}
                  loadingMore={browseLoadingMore === 'customers'}
                  onLoadMore={() => loadMoreBrowse('customers')}
                />
              </Box>
            )}

            {/* Prospects List */}
            {browseTab === 'prospects' && (
              <Box sx={{ pt: 2 }}>
                <RecipientList
                  contacts={getBrowseByType('prospect')}
                  onContactToggle={handleContactToggle}
                  recipients={value.recipients}
                  isLoading={isLoading}
                  type="prospect"
                  onSelectAll={() => selectAllOfType('prospect')}
                  hasMore={prospects.length < browseTotals.prospects}
                  loadedCount={prospects.length}
                  totalCount={browseTotals.prospects}
                  loadingMore={browseLoadingMore === 'prospects'}
                  onLoadMore={() => loadMoreBrowse('prospects')}
                />
              </Box>
            )}

            {/* AI Prospects — THREE recipient pools behind an inner selector:
                  • Qualified (CRM) — the sales agent's prospects already pushed
                    into the CRM, surfaced via their contact emails (browse +
                    select-all, with a guessed/verified flag).
                  • Sales AI leads — every named buyer on the agent's scored
                    leads (agent-only; bulk import into the directory).
                  • Harvester — the agent's blind harvested contact pool
                    (agent-only; bulk import into the directory). */}
            {browseTab === 'ai_prospects' && (
              <Box sx={{ pt: 2 }}>
                <Tabs
                  value={aiSource}
                  onChange={(_, v) => setAiSource(v)}
                  variant="fullWidth"
                  sx={{
                    mb: 1,
                    "& .MuiTab-root": {
                      minWidth: 0,
                      px: 1,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <Tab value="crm" label="Qualified (CRM)" />
                  <Tab value="sales_leads" label="Sales AI leads" />
                  <Tab value="harvester" label="Harvester" />
                </Tabs>

                {aiSource === 'crm' ? (
                  <RecipientList
                    contacts={getBrowseByType('ai_prospect')}
                    onContactToggle={handleContactToggle}
                    recipients={value.recipients}
                    isLoading={isLoading}
                    type="ai_prospect"
                    onSelectAll={() => selectAllOfType('ai_prospect')}
                    onSelectAllVerified={selectVerifiedAiProspects}
                    verifiedCount={aiVerifiedAvailable}
                    showConfidence
                    emptyHint="AI prospects appear here once the sales agent's leads are added to the CRM."
                    hasMore={aiLeads.length < browseTotals.ai_prospects}
                    loadedCount={aiLeads.length}
                    totalCount={browseTotals.ai_prospects}
                    loadingMore={browseLoadingMore === 'ai_prospects'}
                    onLoadMore={() => loadMoreBrowse('ai_prospects')}
                  />
                ) : (
                  renderAiSourcePanel(aiSource)
                )}
              </Box>
            )}

            {/* Contacts List */}
            {browseTab === 'contacts' && (
              <Box sx={{ pt: 2 }}>
                <RecipientList
                  contacts={getBrowseByType('contact')}
                  onContactToggle={handleContactToggle}
                  recipients={value.recipients}
                  isLoading={isLoading}
                  type="contact"
                  onSelectAll={() => selectAllOfType('contact')}
                  hasMore={contacts.length < browseTotals.contacts}
                  loadedCount={contacts.length}
                  totalCount={browseTotals.contacts}
                  loadingMore={browseLoadingMore === 'contacts'}
                  onLoadMore={() => loadMoreBrowse('contacts')}
                />
              </Box>
            )}
          </Box>
        )}

      </Box>
    </Box>
  )
}

